// Color transformation utilities inspired by Midnight Lizard
// Handles HSV transformations for advanced theme controls

export interface HSV {
  h: number; // 0-360
  s: number; // 0-100
  v: number; // 0-100
}

export interface RGB {
  r: number; // 0-255
  g: number; // 0-255
  b: number; // 0-255
}

export interface TMI {
  t: number; // Temperature: 0-100 (0=cool/blue, 50=neutral, 100=warm/orange)
  m: number; // Magenta: 0-100 (0=green, 50=neutral, 100=magenta)
  i: number; // Intensity: 0-100 (brightness, same as HSV Value)
}

export type ColorSpace = 'RGB' | 'HSV' | 'TMI' | 'Off';

/**
 * Convert hex color to RGB
 */
export function hexToRgb(hex: string): RGB | null {
  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Handle 3-digit hex
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }

  if (hex.length !== 6) {
    return null;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return { r, g, b };
}

/**
 * Convert RGB to hex
 */
export function rgbToHex(rgb: RGB): string {
  const toHex = (n: number) => {
    const clamped = Math.max(0, Math.min(255, Math.round(n)));
    return clamped.toString(16).padStart(2, '0');
  };

  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}

/**
 * Convert RGB to HSV
 */
export function rgbToHsv(rgb: RGB): HSV {
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const v = max;

  if (delta !== 0) {
    s = delta / max;

    switch (max) {
      case r:
        h = ((g - b) / delta + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / delta + 2) / 6;
        break;
      case b:
        h = ((r - g) / delta + 4) / 6;
        break;
    }
  }

  return {
    h: h * 360,
    s: s * 100,
    v: v * 100
  };
}

/**
 * Convert HSV to RGB
 */
export function hsvToRgb(hsv: HSV): RGB {
  const h = hsv.h / 360;
  const s = hsv.s / 100;
  const v = hsv.v / 100;

  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);

  let r, g, b;

  switch (i % 6) {
    case 0:
      r = v; g = t; b = p;
      break;
    case 1:
      r = q; g = v; b = p;
      break;
    case 2:
      r = p; g = v; b = t;
      break;
    case 3:
      r = p; g = q; b = v;
      break;
    case 4:
      r = t; g = p; b = v;
      break;
    case 5:
      r = v; g = p; b = q;
      break;
    default:
      r = v; g = t; b = p;
  }

  return {
    r: r * 255,
    g: g * 255,
    b: b * 255
  };
}

/**
 * Convert hex to HSV
 */
export function hexToHsv(hex: string): HSV | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return rgbToHsv(rgb);
}

/**
 * Convert HSV to hex
 */
export function hsvToHex(hsv: HSV): string {
  const rgb = hsvToRgb(hsv);
  return rgbToHex(rgb);
}

/**
 * Convert RGB to TMI (Temperature, Magenta, Intensity)
 * TMI is a perceptual color space useful for color calibration
 * Temperature axis: blue-yellow (cool-warm)
 * Magenta axis: green-magenta
 * Intensity: overall brightness
 */
export function rgbToTmi(rgb: RGB): TMI {
  // Normalize RGB to 0-1
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;

  // Calculate intensity (same as HSV value)
  const i = Math.max(r, g, b) * 100;

  // Calculate temperature (blue-yellow axis)
  // More red/yellow = warmer (higher T), more blue = cooler (lower T)
  // Using a weighted formula: warm colors (red/yellow) vs cool colors (blue)
  const warmth = (r + g * 0.5) / 1.5; // Red and yellow contribute to warmth
  const coolness = b; // Blue is cool
  const t = ((warmth - coolness + 1) / 2) * 100; // Normalize to 0-100

  // Calculate magenta (green-magenta axis)
  // More magenta/red/blue = higher M, more green = lower M
  const magentaness = (r + b) / 2; // Red and blue make magenta
  const greenness = g;
  const m = ((magentaness - greenness + 1) / 2) * 100; // Normalize to 0-100

  return {
    t: Math.max(0, Math.min(100, t)),
    m: Math.max(0, Math.min(100, m)),
    i: Math.max(0, Math.min(100, i))
  };
}

/**
 * Convert TMI to RGB
 */
export function tmiToRgb(tmi: TMI): RGB {
  // Denormalize TMI from 0-100 to working range
  const t = tmi.t / 100; // 0=cool, 0.5=neutral, 1=warm
  const m = tmi.m / 100; // 0=green, 0.5=neutral, 1=magenta
  const i = tmi.i / 100; // 0=black, 1=white

  // Start with neutral gray at given intensity
  let r = i;
  let g = i;
  let b = i;

  // Apply temperature shift
  // t < 0.5: shift toward blue (cool)
  // t > 0.5: shift toward yellow/orange (warm)
  const tempShift = (t - 0.5) * 2; // -1 to +1
  if (tempShift > 0) {
    // Warm: increase red/yellow
    r += tempShift * (1 - r) * 0.8;
    g += tempShift * (1 - g) * 0.4;
    b -= tempShift * b * 0.6;
  } else {
    // Cool: increase blue
    b += Math.abs(tempShift) * (1 - b) * 0.8;
    r -= Math.abs(tempShift) * r * 0.4;
    g -= Math.abs(tempShift) * g * 0.4;
  }

  // Apply magenta shift
  // m < 0.5: shift toward green
  // m > 0.5: shift toward magenta (red+blue)
  const magShift = (m - 0.5) * 2; // -1 to +1
  if (magShift > 0) {
    // Magenta: increase red and blue
    r += magShift * (1 - r) * 0.6;
    b += magShift * (1 - b) * 0.6;
    g -= magShift * g * 0.5;
  } else {
    // Green: increase green
    g += Math.abs(magShift) * (1 - g) * 0.8;
    r -= Math.abs(magShift) * r * 0.4;
    b -= Math.abs(magShift) * b * 0.4;
  }

  return {
    r: Math.max(0, Math.min(255, r * 255)),
    g: Math.max(0, Math.min(255, g * 255)),
    b: Math.max(0, Math.min(255, b * 255))
  };
}

/**
 * Convert hex to TMI
 */
export function hexToTmi(hex: string): TMI | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return rgbToTmi(rgb);
}

/**
 * Convert TMI to hex
 */
export function tmiToHex(tmi: TMI): string {
  const rgb = tmiToRgb(tmi);
  return rgbToHex(rgb);
}

/**
 * Convert between color spaces
 */
export function hsvToTmi(hsv: HSV): TMI {
  const rgb = hsvToRgb(hsv);
  return rgbToTmi(rgb);
}

export function tmiToHsv(tmi: TMI): HSV {
  const rgb = tmiToRgb(tmi);
  return rgbToHsv(rgb);
}

/**
 * Adjust saturation of a color
 * @param hsv HSV color
 * @param saturationLimit 0-100, where 50 = no change
 */
export function adjustSaturation(hsv: HSV, saturationLimit: number): HSV {
  // saturationLimit: 50 = no change, 0 = grayscale, 100 = max saturation
  // Use more subtle adjustment for better control

  if (saturationLimit === 50) return hsv; // No change

  const adjustment = (saturationLimit - 50) / 50; // -1 to +1 range
  let newS = hsv.s;

  if (adjustment > 0) {
    // Increase saturation: move towards 100
    newS = hsv.s + (100 - hsv.s) * adjustment * 0.6;
  } else {
    // Decrease saturation: move towards 0
    newS = hsv.s + hsv.s * adjustment;
  }

  return {
    ...hsv,
    s: Math.max(0, Math.min(100, newS))
  };
}

/**
 * Adjust brightness (value) of a color
 * @param hsv HSV color
 * @param brightnessLimit 0-100, where 50 = no change
 */
export function adjustBrightness(hsv: HSV, brightnessLimit: number): HSV {
  // brightnessLimit: 50 = no change, 0 = very dark, 100 = very bright
  // Use a more subtle adjustment: shift value by a percentage

  if (brightnessLimit === 50) return hsv; // No change

  const adjustment = (brightnessLimit - 50) / 50; // -1 to +1 range
  let newV = hsv.v;

  if (adjustment > 0) {
    // Brighten: move towards 100
    newV = hsv.v + (100 - hsv.v) * adjustment * 0.5;
  } else {
    // Darken: move towards 0
    newV = hsv.v + hsv.v * adjustment * 0.5;
  }

  return {
    ...hsv,
    v: Math.max(0, Math.min(100, newV))
  };
}

/**
 * Adjust contrast by pushing value away from middle gray
 * In HSV, we adjust the value (brightness) component
 * @param hsv HSV color
 * @param contrastLevel 0-100, where 50 = no change
 */
export function adjustContrast(hsv: HSV, contrastLevel: number): HSV {
  // contrastLevel: 50 = no change, higher = more contrast, lower = less contrast
  // Push colors away from or toward middle gray (50% value)

  if (contrastLevel === 50) return hsv; // No change

  const adjustment = (contrastLevel - 50) / 50; // -1 to +1 range
  const middle = 50;
  const distance = hsv.v - middle;

  // Adjust the distance from middle: positive adjustment = more contrast
  const newDistance = distance * (1 + adjustment * 0.5);
  const newV = middle + newDistance;

  return {
    ...hsv,
    v: Math.max(0, Math.min(100, newV))
  };
}

/**
 * Calculate shortest distance between two hues on color wheel
 */
function hueDistance(hue1: number, hue2: number): number {
  const diff = Math.abs(hue1 - hue2);
  return diff > 180 ? 360 - diff : diff;
}

/**
 * Apply hue gravity - pull color toward target hue
 * Inspired by Midnight Lizard's hue shift algorithm
 * @param hsv HSV color
 * @param targetHue 0-360 degrees
 * @param gravity 0-100, strength of pull toward target hue
 */
export function applyHueGravity(hsv: HSV, targetHue: number, gravity: number): HSV {
  if (gravity === 0) return hsv;

  // Normalize hues to 0-360
  const currentHue = hsv.h % 360;
  const target = targetHue % 360;

  // Calculate shortest path on color wheel
  let diff = target - currentHue;
  if (diff > 180) diff -= 360;
  if (diff < -180) diff += 360;

  // Apply gravity as percentage of distance to target
  // Higher gravity = stronger pull
  const shift = diff * (gravity / 100);

  // Non-linear scaling inspired by Midnight Lizard's arc-tangent approach
  // This prevents extreme shifts and keeps colors natural
  const scaledShift = Math.sign(shift) * Math.min(
    Math.abs(shift),
    60 * Math.atan(Math.abs(shift) / 60) // Max shift approaches 60 degrees
  );

  const newHue = (currentHue + scaledShift + 360) % 360;

  return {
    ...hsv,
    h: newHue
  };
}

/**
 * Apply gray saturation - add subtle color to gray tones
 * @param hsv HSV color
 * @param graySaturation 0-100
 * @param targetHue 0-360 degrees
 */
export function applyGraySaturation(hsv: HSV, graySaturation: number, targetHue: number): HSV {
  if (graySaturation === 0 || hsv.s > 10) return hsv; // Only affect grays

  // Add subtle saturation to grays, tinted toward target hue
  const boostedSaturation = Math.min(30, hsv.s + (graySaturation / 100) * 20);

  return {
    h: targetHue, // Shift gray toward target hue
    s: boostedSaturation,
    v: hsv.v
  };
}

// ============================================
// TMI Adjustment Functions
// ============================================

/**
 * Adjust temperature in TMI space
 * @param tmi TMI color
 * @param temperatureLimit 0-100, where 50 = no change
 */
export function adjustTemperature(tmi: TMI, temperatureLimit: number): TMI {
  if (temperatureLimit === 50) return tmi;

  const adjustment = (temperatureLimit - 50) / 50; // -1 to +1
  let newT = tmi.t;

  if (adjustment > 0) {
    // Warmer: move toward 100
    newT = tmi.t + (100 - tmi.t) * adjustment * 0.6;
  } else {
    // Cooler: move toward 0
    newT = tmi.t + tmi.t * adjustment;
  }

  return {
    ...tmi,
    t: Math.max(0, Math.min(100, newT))
  };
}

/**
 * Adjust magenta in TMI space
 * @param tmi TMI color
 * @param magentaLimit 0-100, where 50 = no change
 */
export function adjustMagenta(tmi: TMI, magentaLimit: number): TMI {
  if (magentaLimit === 50) return tmi;

  const adjustment = (magentaLimit - 50) / 50; // -1 to +1
  let newM = tmi.m;

  if (adjustment > 0) {
    // More magenta: move toward 100
    newM = tmi.m + (100 - tmi.m) * adjustment * 0.6;
  } else {
    // More green: move toward 0
    newM = tmi.m + tmi.m * adjustment;
  }

  return {
    ...tmi,
    m: Math.max(0, Math.min(100, newM))
  };
}

/**
 * Adjust intensity in TMI space
 * @param tmi TMI color
 * @param intensityLimit 0-100, where 50 = no change
 */
export function adjustIntensity(tmi: TMI, intensityLimit: number): TMI {
  if (intensityLimit === 50) return tmi;

  const adjustment = (intensityLimit - 50) / 50; // -1 to +1
  let newI = tmi.i;

  if (adjustment > 0) {
    // Brighter: move toward 100
    newI = tmi.i + (100 - tmi.i) * adjustment * 0.5;
  } else {
    // Darker: move toward 0
    newI = tmi.i + tmi.i * adjustment * 0.5;
  }

  return {
    ...tmi,
    i: Math.max(0, Math.min(100, newI))
  };
}

// ============================================
// RGB Adjustment Functions
// ============================================

/**
 * Adjust red channel
 */
export function adjustRed(rgb: RGB, redLimit: number): RGB {
  if (redLimit === 50) return rgb;

  const adjustment = (redLimit - 50) / 50; // -1 to +1
  let newR = rgb.r;

  if (adjustment > 0) {
    newR = rgb.r + (255 - rgb.r) * adjustment * 0.6;
  } else {
    newR = rgb.r + rgb.r * adjustment;
  }

  return {
    ...rgb,
    r: Math.max(0, Math.min(255, newR))
  };
}

/**
 * Adjust green channel
 */
export function adjustGreen(rgb: RGB, greenLimit: number): RGB {
  if (greenLimit === 50) return rgb;

  const adjustment = (greenLimit - 50) / 50; // -1 to +1
  let newG = rgb.g;

  if (adjustment > 0) {
    newG = rgb.g + (255 - rgb.g) * adjustment * 0.6;
  } else {
    newG = rgb.g + rgb.g * adjustment;
  }

  return {
    ...rgb,
    g: Math.max(0, Math.min(255, newG))
  };
}

/**
 * Adjust blue channel
 */
export function adjustBlue(rgb: RGB, blueLimit: number): RGB {
  if (blueLimit === 50) return rgb;

  const adjustment = (blueLimit - 50) / 50; // -1 to +1
  let newB = rgb.b;

  if (adjustment > 0) {
    newB = rgb.b + (255 - rgb.b) * adjustment * 0.6;
  } else {
    newB = rgb.b + rgb.b * adjustment;
  }

  return {
    ...rgb,
    b: Math.max(0, Math.min(255, newB))
  };
}

/**
 * Apply all advanced color transformations to a hex color
 */
export interface ColorTransformOptions {
  saturationLimit?: number; // 0-100, default 50 (no change)
  brightnessLimit?: number; // 0-100, default 50 (no change)
  contrast?: number; // 0-100, default 50 (no change)
  graySaturation?: number; // 0-100, default 0
  hueGravity?: number; // 0-100, default 0
  targetHue?: number; // 0-360, default 184 (teal)
  colorSpace?: ColorSpace; // Which color space to use for transformations
  // TMI-specific options
  temperatureLimit?: number; // 0-100, default 50
  magentaLimit?: number; // 0-100, default 50
  intensityLimit?: number; // 0-100, default 50
  // RGB-specific options
  redLimit?: number; // 0-100, default 50
  greenLimit?: number; // 0-100, default 50
  blueLimit?: number; // 0-100, default 50
  // Background-specific aliases (used by ProfileClient/CommunityClient)
  backgroundSaturationLimit?: number; // Alias for saturationLimit
  backgroundBrightnessLimit?: number; // Alias for brightnessLimit
  backgroundContrast?: number; // Alias for contrast
  backgroundHueGravity?: number; // Alias for hueGravity
  backgroundGraySaturation?: number; // Alias for graySaturation
  backgroundDefaultHue?: number; // Alias for targetHue
}

export function transformColor(hexColor: string, options: ColorTransformOptions): string {
  // If colorSpace is 'Off', return the color unchanged
  if (options.colorSpace === 'Off') {
    return hexColor;
  }

  // Map background aliases to their proper names
  const saturationLimit = options.saturationLimit ?? options.backgroundSaturationLimit ?? 50;
  const brightnessLimit = options.brightnessLimit ?? options.backgroundBrightnessLimit ?? 50;
  const contrast = options.contrast ?? options.backgroundContrast ?? 50;
  const graySaturation = options.graySaturation ?? options.backgroundGraySaturation ?? 0;
  const hueGravity = options.hueGravity ?? options.backgroundHueGravity ?? 0;
  const targetHue = options.targetHue ?? options.backgroundDefaultHue ?? 184;

  const {
    colorSpace = 'HSV',
    // TMI options
    temperatureLimit = 50,
    magentaLimit = 50,
    intensityLimit = 50,
    // RGB options
    redLimit = 50,
    greenLimit = 50,
    blueLimit = 50
  } = options;

  // Apply transformations based on selected color space
  if (colorSpace === 'TMI') {
    // TMI color space transformations
    let tmi = hexToTmi(hexColor);
    if (!tmi) return hexColor;

    const originalTmi = { ...tmi };

    tmi = adjustTemperature(tmi, temperatureLimit);
    tmi = adjustMagenta(tmi, magentaLimit);
    tmi = adjustIntensity(tmi, intensityLimit);

    const result = tmiToHex(tmi);

    if (result !== hexColor) {
    }

    return result;

  } else if (colorSpace === 'RGB') {
    // RGB color space transformations
    let rgb = hexToRgb(hexColor);
    if (!rgb) return hexColor;

    const originalRgb = { ...rgb };

    rgb = adjustRed(rgb, redLimit);
    rgb = adjustGreen(rgb, greenLimit);
    rgb = adjustBlue(rgb, blueLimit);

    const result = rgbToHex(rgb);

    if (result !== hexColor) {
    }

    return result;

  } else {
    // HSV color space transformations (default)
    let hsv = hexToHsv(hexColor);
    if (!hsv) return hexColor;

    const originalHsv = { ...hsv };

    // Apply hue shift first (direct hue rotation)
    // targetHue represents the desired hue shift in degrees (0-360)
    // We shift the hue by the difference from the neutral point (184°)
    if (targetHue !== 184) {
      const hueShiftAmount = targetHue - 184; // Amount to shift from neutral
      hsv.h = (hsv.h + hueShiftAmount + 360) % 360; // Apply shift and wrap around
    }

    // Apply transformations in order
    hsv = adjustSaturation(hsv, saturationLimit);
    hsv = adjustBrightness(hsv, brightnessLimit);
    hsv = adjustContrast(hsv, contrast);

    // Apply gray saturation before hue gravity
    if (graySaturation > 0) {
      hsv = applyGraySaturation(hsv, graySaturation, targetHue);
    }

    // Apply hue gravity last
    if (hueGravity > 0) {
      hsv = applyHueGravity(hsv, targetHue, hueGravity);
    }

    const result = hsvToHex(hsv);

    if (result !== hexColor) {
    }

    return result;
  }
}
